import { openDatabase } from '@/db/schema'
import { repo } from '@/db/repo'
import { buildArrangements, buildSongs } from '@/db/seed.songs'
import {
  buildMemberships,
  buildMinistries,
  buildPeople,
  buildPositions,
  buildServiceTypes,
  buildTeams,
} from '@/db/seed.org'
import {
  buildApiKeys,
  buildBlockouts,
  buildNotifications,
  buildPlans,
  buildSettings,
} from '@/db/seed.plans'

export { CURRENT_PERSON_ID } from '@/db/seed.org'

/**
 * Populates an empty database with a full demo organisation.
 *
 * Idempotent: a database that already holds people is left untouched unless
 * `force` is set (Admin -> Danger zone -> "Reset demo data").
 */
export async function seedDatabase(force = false): Promise<boolean> {
  await openDatabase()
  const existing = await repo.count('people')
  if (existing > 0 && !force) return false

  if (force) await repo.clearAll()

  const { plans, planItems, assignments } = buildPlans()

  await Promise.all([
    repo.putMany('ministries', buildMinistries()),
    repo.putMany('teams', buildTeams()),
    repo.putMany('positions', buildPositions()),
    repo.putMany('people', buildPeople()),
    repo.putMany('memberships', buildMemberships()),
    repo.putMany('serviceTypes', buildServiceTypes()),
    repo.putMany('songs', buildSongs()),
    repo.putMany('arrangements', buildArrangements()),
    repo.putMany('plans', plans),
    repo.putMany('planItems', planItems),
    repo.putMany('assignments', assignments),
    repo.putMany('blockouts', buildBlockouts()),
    repo.putMany('notifications', buildNotifications()),
    repo.putMany('apiKeys', buildApiKeys()),
    repo.put('settings', buildSettings()),
  ])

  return true
}

/** Ensures a settings row exists even when demo seeding is disabled. */
export async function ensureSettings(): Promise<void> {
  const existing = await repo.get('settings', 'app-settings')
  if (!existing) await repo.put('settings', buildSettings())
}
